/**
 * The upgrade to one queue (FEAT-038 wave 2).
 *
 * Two databases are being retired: `momento-drafts` (the drafts store) and
 * `momento-uploads` (the send outbox and its upload sessions). Everything they
 * hold moves into `momento-media` — drafts become `media-meta` rows with
 * `draft: true`, queued sends become ledger rows — and then, and only then,
 * the empty databases are deleted.
 *
 * Three rules this file is built around:
 *
 * - **Never guess a recipient.** A queued row whose destination is missing is
 *   exactly BUG-119: a send that forgot where it was going. It becomes a
 *   DRAFT, so the user re-sends it deliberately. Inventing a recipient for
 *   someone's private moment is the one mistake with no undo.
 * - **Never read a record that killed a launch.** Wave 1's evacuator parks
 *   those in `evacuate-failed`; this skips exactly the same set, and leaves the
 *   database they live in alone.
 * - **Adopt, don't restart.** A row's existing client key, an unfinished upload
 *   session's offset, and a receipt for bytes already on the server all carry
 *   across. A send that half-happened must not happen again from zero.
 */
import { mediaStore } from '../media/mediaStore';
import { OUTBOX_MEDIA_PREFIX } from '../media/evacuate';
import { ledger } from './ledger';
import type { Destination, QueueRow, SessionRow } from './types';

const DRAFTS_DB = 'momento-drafts';
const DRAFTS_STORE = 'captures';
const OUTBOX_DB = 'momento-uploads';
const OUTBOX_STORE = 'pending-uploads';
const LEGACY_SESSION_STORE = 'upload-sessions';

const FAILED_KEY = 'evacuate-failed';

const OPEN_TIMEOUT_MS = 5_000;

type LegacyRow = Record<string, unknown> & { id: string };

interface FailedSet {
  ids: string[];
}

// ---- raw IDB against databases no service owns any more --------------------

function reqOf<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

/** Open a legacy database WITHOUT creating it: a versionless open of an absent
 * name creates an empty database, and an empty `momento-drafts` would then take
 * its v1→v2 upgrade path on a phone that never had a draft. */
async function openLegacy(name: string): Promise<IDBDatabase | null> {
  if (typeof indexedDB === 'undefined') return null;
  if (typeof indexedDB.databases === 'function') {
    try {
      const known = await indexedDB.databases();
      if (!known.some((entry) => entry.name === name)) return null;
    } catch {
      // Not available (older Safari) — fall through to the tolerant open.
    }
  }
  return new Promise<IDBDatabase | null>((resolve) => {
    let settled = false;
    const finish = (db: IDBDatabase | null) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      resolve(db);
    };
    const timer = setTimeout(() => finish(null), OPEN_TIMEOUT_MS);
    const request = indexedDB.open(name);
    request.onsuccess = () => finish(request.result);
    request.onerror = () => finish(null);
    request.onblocked = () => finish(null);
  });
}

const keysOf = (db: IDBDatabase, store: string): Promise<string[]> =>
  db.objectStoreNames.contains(store)
    ? (reqOf(
        db.transaction(store, 'readonly').objectStore(store).getAllKeys()
      ) as Promise<string[]>)
    : Promise.resolve([]);

const rowOf = (db: IDBDatabase, store: string, id: string): Promise<LegacyRow | undefined> =>
  reqOf(db.transaction(store, 'readonly').objectStore(store).get(id)) as Promise<
    LegacyRow | undefined
  >;

function deleteRow(db: IDBDatabase, store: string, id: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(store, 'readwrite');
    tx.objectStore(store).delete(id);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error ?? new Error('delete aborted'));
  });
}

/** Yield between records: this runs ahead of first paint, and the GC needs a
 * turn to release one record before the next is read. */
const yieldTurn = () => new Promise((resolve) => setTimeout(resolve, 0));

const isFat = (row: LegacyRow): boolean =>
  !!row.bytes || !!row.legacyBlob || !!row.mediaBlob;

/** Deliberately duck-typed, not `instanceof`: a structured clone can come back
 * from another realm (it does under fake-indexeddb), and a legacy record read
 * as "not really an ArrayBuffer" is a moment silently sized at zero. */
function blobOf(row: LegacyRow): Blob | null {
  if (row.bytes) {
    return new Blob([row.bytes as BlobPart], { type: (row.blobType as string) || '' });
  }
  const blob = (row.legacyBlob ?? row.mediaBlob) as Blob | undefined;
  return blob && typeof blob.size === 'number' ? blob : null;
}

// ---- outbox rows -----------------------------------------------------------

/**
 * Where a queued row was going, or null if it no longer knows — which is the
 * bug this wave closes by construction, and the one case where the answer is
 * "ask the user", never "pick someone".
 */
function destinationOf(row: LegacyRow): Destination | null {
  switch (row.type) {
    case 'message':
      return typeof row.recipientId === 'number'
        ? { kind: 'friend', friendId: row.recipientId }
        : null;
    case 'group':
      return typeof row.groupId === 'number'
        ? { kind: 'group', groupId: row.groupId }
        : null;
    case 'story':
      return { kind: 'story', audience: 'everyone' };
    case 'vault':
      return {
        kind: 'vault',
        isPrivate: Boolean(row.isPrivate),
        albumId: row.albumId as number | undefined,
      };
    default:
      return null;
  }
}

async function adoptOutboxRow(
  db: IDBDatabase,
  row: LegacyRow,
  parked: Set<string>
): Promise<void> {
  const mediaId = OUTBOX_MEDIA_PREFIX + row.id;
  const fat = isFat(row);
  const meta = await mediaStore.meta(mediaId);
  const size =
    (row.sizeBytes as number | undefined) ?? meta?.size ?? blobOf(row)?.size ?? 0;
  const dest = destinationOf(row);

  if (!dest) {
    // BUG-119's victims. The media is safe; the recipient is not knowable. It
    // becomes an unsent moment the user can send again on purpose.
    console.warn('[Queue] a queued row had no destination — kept as a draft:', row.id);
    if (meta) {
      await mediaStore.updateMeta(mediaId, (current) => ({ ...current, draft: true }));
      await deleteRow(db, OUTBOX_STORE, row.id);
    } else if (fat && !parked.has(row.id)) {
      const blob = blobOf(row);
      if (blob) {
        await mediaStore.saveBlob(
          {
            id: mediaId,
            userId: 0,
            mediaType: (row.mediaType as 'photo' | 'video') ?? 'photo',
            mimeType: blob.type,
            width: 0,
            height: 0,
            facing: row.facing as 'user' | 'environment' | undefined,
            hd: row.hd as boolean | undefined,
            kind: 'single',
            textBars: [],
            drawingStrokes: [],
            overlays: (row.overlays as never) ?? null,
            draft: true,
            createdAt: (row.createdAt as number) ?? Date.now(),
          },
          blob,
          { bypassBudget: true }
        );
        await deleteRow(db, OUTBOX_STORE, row.id);
      }
    }
    return;
  }

  // Its own key wins: a row that already went out under one must converge on
  // the server, not arrive twice under a freshly derived name.
  const clientKey =
    (row.clientKey as string | undefined) ||
    `${mediaId}:${dest.kind === 'friend' ? `f${dest.friendId}` : dest.kind === 'group' ? `g${dest.groupId}` : dest.kind === 'story' ? 's' : `v${dest.isPrivate ? 'p' : ''}`}`;
  if (ledger.get(clientKey)) return;

  const now = Date.now();
  const queued: QueueRow = {
    id: clientKey,
    mediaId,
    clientKey,
    dest,
    state: row.status === 'stuck' ? 'stuck' : 'queued',
    size,
    mediaType: (row.mediaType as 'photo' | 'video') ?? 'photo',
    caption: row.caption as string | undefined,
    overlays: (row.overlays as never) ?? null,
    retentionType: row.retentionType as string | undefined,
    replayAllowed: row.replayAllowed as boolean | undefined,
    hd: row.hd as boolean | undefined,
    facing: row.facing as 'user' | 'environment' | undefined,
    draftId: row.draftId as string | undefined,
    optimisticId: row.optimisticMessageId as string | undefined,
    // A record wave 1 could not thin keeps its bytes where they are and is read
    // ONCE, at its upload turn — never at boot, which is what made them fatal.
    legacyRowId: fat ? row.id : undefined,
    createdAt: (row.createdAt as number) ?? now,
    updatedAt: now,
    attempts: (row.retryCount as number) ?? 0,
    lastError: row.lastError as string | undefined,
    stuckReason: row.status === 'stuck' ? (row.lastError as string) : undefined,
  };
  await ledger.put(queued);

  // The shutter's upload, if this row was already holding one — the bytes are
  // on the server and must not go up again.
  if (typeof row.stagedMediaId === 'string') {
    await ledger.putSession({
      mediaId,
      uploadId: null,
      committedOffset: size,
      hashedBytes: 0,
      totalBytes: size,
      stagedId: row.stagedMediaId,
      stagedAt: (row.createdAt as number) ?? now,
      updatedAt: now,
    });
  }

  if (!fat) await deleteRow(db, OUTBOX_STORE, row.id);
}

/**
 * A parked record's bytes, read at its upload turn and nowhere else.
 *
 * The whole point of the deferral: a record wave 1 could not thin (the phone
 * was out of room) is still a send that must go, and reading it once while its
 * upload is actually moving costs one resident copy, at the one moment that is
 * what memory is for.
 */
export async function legacyOutboxBlob(rowId: string): Promise<Blob | null> {
  const db = await openLegacy(OUTBOX_DB);
  if (!db) return null;
  try {
    if (!db.objectStoreNames.contains(OUTBOX_STORE)) return null;
    const row = await rowOf(db, OUTBOX_STORE, rowId);
    return row ? blobOf(row) : null;
  } catch (err) {
    console.error('[Queue] could not read a parked record:', err);
    return null;
  } finally {
    db.close();
  }
}

// ---- upload sessions -------------------------------------------------------

/** Sessions and receipts the old client keyed by capture id / `outbox:<id>` /
 * `done:<id>`. Re-keyed by media so the engine picks up exactly where the last
 * build left off — an unfinished 40 MB upload resumes at its offset instead of
 * starting again. */
async function adoptSessions(db: IDBDatabase): Promise<void> {
  if (!db.objectStoreNames.contains(LEGACY_SESSION_STORE)) return;
  const keys = await keysOf(db, LEGACY_SESSION_STORE);
  for (const key of keys) {
    const row = (await rowOf(db, LEGACY_SESSION_STORE, key)) as
      | (LegacyRow & {
          key: string;
          uploadId: string;
          stagedId?: string;
          committedOffset: number;
          totalBytes: number | null;
          createdAt: number;
        })
      | undefined;
    if (!row) continue;
    const receipt = key.startsWith('done:');
    const bare = receipt
      ? key.slice('done:'.length)
      : key.startsWith('outbox:')
        ? key.slice('outbox:'.length)
        : key;
    const mediaId = key.startsWith('outbox:') ? OUTBOX_MEDIA_PREFIX + bare : bare;
    if (!(await mediaStore.meta(mediaId))) continue; // its media is long gone
    const existing = await ledger.session(mediaId);
    const session: SessionRow = {
      mediaId,
      uploadId: receipt ? null : row.uploadId,
      committedOffset: receipt ? 0 : (row.committedOffset ?? 0),
      hashedBytes: 0,
      totalBytes: row.totalBytes ?? 0,
      stagedId: receipt ? row.stagedId : existing?.stagedId,
      stagedAt: receipt ? row.createdAt : existing?.stagedAt,
      updatedAt: Date.now(),
    };
    await ledger.putSession({ ...existing, ...session });
    await deleteRow(db, LEGACY_SESSION_STORE, key);
  }
}

// ---- draft rows ------------------------------------------------------------

async function adoptDraftRow(
  db: IDBDatabase,
  row: LegacyRow,
  parked: Set<string>
): Promise<void> {
  const labels = {
    userId: (row.userId as number) ?? 0,
    kind: (row.kind as 'single' | 'batch-shot') ?? 'single',
    batchId: row.batchId as string | undefined,
    batchIndex: row.batchIndex as number | undefined,
    textBars: (row.textBars as { text: string; y: number }[]) ?? [],
    drawingStrokes: (row.drawingStrokes as never[]) ?? [],
    overlays: (row.overlays as never) ?? null,
    sentKeys: row.sentKeys as string[] | undefined,
    sentAt: row.sentAt as number | undefined,
    draft: true as const,
  };

  if (!isFat(row)) {
    // Wave 1 already moved its bytes; this row is the labels, and the legacy
    // record has been the only place edits landed since.
    const updated = await mediaStore.updateMeta(row.id, (current) => ({
      ...current,
      ...labels,
    }));
    if (updated) await deleteRow(db, DRAFTS_STORE, row.id);
    return;
  }
  if (parked.has(row.id)) return; // it killed a launch once; never again
  const blob = blobOf(row);
  if (!blob || blob.size === 0) return;
  await mediaStore.saveBlob(
    {
      ...labels,
      id: row.id,
      mediaType: (row.mediaType as 'photo' | 'video') ?? 'photo',
      mimeType: blob.type,
      width: (row.width as number) ?? 0,
      height: (row.height as number) ?? 0,
      duration: row.duration as number | undefined,
      facing: row.facing as 'user' | 'environment' | undefined,
      hd: row.hd as boolean | undefined,
      createdAt: (row.createdAt as number) ?? Date.now(),
    },
    blob,
    { bypassBudget: true }
  );
  await deleteRow(db, DRAFTS_STORE, row.id);
}

// ---- the run ---------------------------------------------------------------

/** Delete a retired database, but ONLY once every store in it is empty — a
 * record we could not move is a moment, and a moment is never deleted to tidy
 * up a schema. Keys only; nothing is read. */
async function dropIfEmpty(db: IDBDatabase, name: string): Promise<void> {
  for (const store of Array.from(db.objectStoreNames)) {
    const keys = await keysOf(db, store);
    if (keys.length > 0) {
      console.info(`[Queue] ${name} still holds ${keys.length} record(s) — kept`);
      db.close();
      return;
    }
  }
  db.close();
  await new Promise<void>((resolve) => {
    const request = indexedDB.deleteDatabase(name);
    request.onsuccess = () => resolve();
    request.onerror = () => resolve();
    request.onblocked = () => resolve();
  });
  console.info(`[Queue] ${name} was empty and is gone`);
}

/**
 * Media nobody owns: not a draft, and no ledger row pointing at it. The
 * leavings of a send whose row was migrated away or discarded by an older
 * build. It becomes a draft rather than being deleted — his bytes are never
 * thrown away to tidy up, and a draft is visible, sendable and deletable.
 */
async function adoptUnownedMedia(): Promise<void> {
  const owned = new Set(ledger.all().map((row) => row.mediaId));
  for (const id of await mediaStore.allMetaIds()) {
    if (owned.has(id)) continue;
    const meta = await mediaStore.meta(id);
    if (!meta || meta.draft) continue;
    console.warn('[Queue] media with no owner kept as a draft:', id);
    await mediaStore.updateMeta(id, (current) => ({ ...current, draft: true }));
  }
}

export async function runQueueMigration(): Promise<void> {
  const outbox = await openLegacy(OUTBOX_DB);
  const draftsDb = await openLegacy(DRAFTS_DB);
  if (!outbox && !draftsDb) return;

  const parked = new Set(
    ((await ledger.kvGet<FailedSet>(FAILED_KEY))?.ids ?? []).map((slug) =>
      slug.includes('/') ? slug.slice(slug.indexOf('/') + 1) : slug
    )
  );

  try {
    if (outbox && outbox.objectStoreNames.contains(OUTBOX_STORE)) {
      for (const id of await keysOf(outbox, OUTBOX_STORE)) {
        if (parked.has(id)) continue; // reading it is what killed a launch
        try {
          const row = await rowOf(outbox, OUTBOX_STORE, id);
          if (row) await adoptOutboxRow(outbox, row, parked);
        } catch (err) {
          console.error(`[Queue] could not adopt queued row ${id}:`, err);
        }
        await yieldTurn();
      }
      await adoptSessions(outbox).catch((err) =>
        console.error('[Queue] could not adopt upload sessions:', err)
      );
    }

    if (draftsDb && draftsDb.objectStoreNames.contains(DRAFTS_STORE)) {
      for (const id of await keysOf(draftsDb, DRAFTS_STORE)) {
        if (parked.has(id)) continue;
        try {
          const row = await rowOf(draftsDb, DRAFTS_STORE, id);
          if (row) await adoptDraftRow(draftsDb, row, parked);
        } catch (err) {
          console.error(`[Queue] could not adopt draft ${id}:`, err);
        }
        await yieldTurn();
      }
    }

    await adoptUnownedMedia();
    await mediaStore.sweepOrphanChunks();
  } finally {
    if (outbox) await dropIfEmpty(outbox, OUTBOX_DB).catch(() => outbox.close());
    if (draftsDb) await dropIfEmpty(draftsDb, DRAFTS_DB).catch(() => draftsDb.close());
  }
}
